import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowsSwapVerticalIcon from 'LuxKit/Icons/line/LineArrowsSwapVerticalIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineEyeIcon from 'LuxKit/Icons/line/LineEyeIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import type TripListView from './TripListView'
import TripListMoveDayMenuItem from './TripListMoveDayMenuItem'
import {
  canAskAiToMoveTripListRow,
  canMoveTripListRowToAnotherDay,
  getTripListMoveDayOptions,
  type TripListMoveDayOption,
} from './tripListMoveDayOptions'
import type { TripListRow } from './tripListTypes'

type TripListViewProps = ComponentProps<typeof TripListView>
type ActionMenuView = 'actions' | 'moveDay'

interface Props {
  row: TripListRow
  tripStops: TripListViewProps['tripStops']
  isBookingPending: boolean
  notesDisabled: boolean
  actionsDisabled: boolean
  onViewRow: () => void
  onBookRow: TripListViewProps['onBookRow']
  onViewBooking: TripListViewProps['onViewBooking']
  onAskAi: TripListViewProps['onAskAi']
  onAskAiMove: TripListViewProps['onAskAiMove']
  onDelete: TripListViewProps['onDelete']
  onMoveToDay: TripListViewProps['onMoveToDay']
  onEditNote: () => void
}

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
`

function TripListMobileRowActionsMenu({
  row,
  tripStops,
  isBookingPending,
  notesDisabled,
  actionsDisabled,
  onViewRow,
  onBookRow,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onDelete,
  onMoveToDay,
  onEditNote,
}: Props) {
  const intl = useIntl()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuView, setMenuView] = useState<ActionMenuView>('actions')
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const canView = row.viewTarget.kind !== 'none'
  const canMoveToAnotherDay = canMoveTripListRowToAnotherDay(row)
  const canAskAiToMove = canAskAiToMoveTripListRow(row)
  const moveDayOptions = useMemo(
    () => canMoveToAnotherDay ? getTripListMoveDayOptions(row, tripStops, intl) : [],
    [canMoveToAnotherDay, intl, row, tripStops],
  )

  useEffect(() => {
    if (actionsDisabled) {
      setMenuOpen(false)
      setMenuView('actions')
    }
  }, [actionsDisabled])

  const viewActionLabel = intl.formatMessage({
    id: '9V1THFwaUjdBLk4x3kKO',
    defaultMessage: 'View',
    description: 'CTA button label for viewing rows in the trip planner list view',
  })
  const bookLabel = intl.formatMessage(
    {
      id: 'SsMSt7B_ipdlo73si5TR',
      defaultMessage: 'Book',
      description: 'Menu item label for booking a row in the mobile TripChat itinerary list view',
    },
  )
  const viewBookingLabel = intl.formatMessage(
    {
      id: 'A3yR4ZovyxC-o5qR5DSO',
      defaultMessage: 'Manage booking',
      description: 'Menu item label for opening a completed booking from the mobile TripChat list view',
    },
  )
  const askAiLabel = intl.formatMessage(
    {
      id: 'v8jAuG2lPFSe0gEMuxeg',
      defaultMessage: 'Ask AI',
      description: 'Menu item label for asking AI about a row in the mobile TripChat itinerary list view',
    },
  )
  const moveToAnotherDayLabel = intl.formatMessage({
    id: 'UBTlxqigTEU0D7XvsI4H',
    defaultMessage: 'Move to another day',
    description: 'Dropdown title for mobile TripChat itinerary list move-day options',
  })
  const moveWithAiLabel = intl.formatMessage({
    id: 'ISgxnzpEfQt0xaR8URkf',
    defaultMessage: 'Move with AI',
    description: 'Menu item label for asking AI to move a mobile TripChat itinerary list row',
  })
  const noteActionLabel = intl.formatMessage({
    id: 'FGUHfwEkhfaRZd_srUta',
    defaultMessage: 'Note',
    description: 'Menu item label for adding or editing a note on a mobile TripChat itinerary list row',
  })
  const deleteLabel = intl.formatMessage({
    id: 's4Y-TiLm1KfiEj0nCz4y',
    defaultMessage: 'Delete',
    description: 'Menu item label for deleting a row in the mobile TripChat itinerary list view',
  })
  const moreActionsLabel = intl.formatMessage(
    {
      id: 'lwXy0ESWfgPHAcbNIY3l',
      defaultMessage: 'More actions for {title}',
      description: 'Accessible label for the trip planner list row actions menu button',
    },
    { title: row.title },
  )

  const handleControlClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const toggleMenu = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    setMenuOpen(prev => {
      const nextOpen = !prev
      if (nextOpen) setMenuView('actions')
      return nextOpen
    })
  }, [actionsDisabled])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    setMenuView('actions')
  }, [])

  const handleViewClick = useCallback(() => {
    if (actionsDisabled || !canView) return
    closeMenu()
    onViewRow()
  }, [actionsDisabled, canView, closeMenu, onViewRow])

  const handleBookClick = useCallback(() => {
    if (actionsDisabled || isBookingPending) return
    closeMenu()
    onBookRow(row)
  }, [actionsDisabled, closeMenu, isBookingPending, onBookRow, row])

  const handleViewBookingClick = useCallback(() => {
    if (actionsDisabled) return
    closeMenu()
    onViewBooking(row)
  }, [actionsDisabled, closeMenu, onViewBooking, row])

  const getMenuOrigin = useCallback((): { x: number; y: number } | undefined => {
    const rect = menuTriggerRef.current?.getBoundingClientRect()
    if (!rect) return undefined
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
  }, [])

  const handleAskAiClick = useCallback(() => {
    if (actionsDisabled) return
    const origin = getMenuOrigin()
    closeMenu()
    onAskAi(row, origin)
  }, [actionsDisabled, closeMenu, getMenuOrigin, onAskAi, row])

  const handleAskAiMoveClick = useCallback(() => {
    if (actionsDisabled || !canAskAiToMove) return
    const origin = getMenuOrigin()
    closeMenu()
    onAskAiMove(row, origin)
  }, [actionsDisabled, canAskAiToMove, closeMenu, getMenuOrigin, onAskAiMove, row])

  const handleMoveDayMenuOpen = useCallback(() => {
    if (actionsDisabled || !canMoveToAnotherDay) return
    setMenuView('moveDay')
  }, [actionsDisabled, canMoveToAnotherDay])

  const handleMoveDayOptionClick = useCallback((option: TripListMoveDayOption) => {
    if (actionsDisabled || option.selected) return
    closeMenu()
    onMoveToDay(row, option)
  }, [actionsDisabled, closeMenu, onMoveToDay, row])

  const handleDeleteClick = useCallback(() => {
    if (actionsDisabled) return
    closeMenu()
    onDelete(row)
  }, [actionsDisabled, closeMenu, onDelete, row])

  const handleEditNoteClick = useCallback(() => {
    if (notesDisabled) return
    closeMenu()
    onEditNote()
  }, [closeMenu, notesDisabled, onEditNote])

  return (
    <RowActions onClick={handleControlClick}>
      <IconButton
        size="small"
        kind="tertiary"
        onClick={toggleMenu}
        ref={menuTriggerRef}
        disabled={actionsDisabled}
        aria-label={moreActionsLabel}
      >
        <LineEllipsisVIcon />
      </IconButton>
      <ResponsiveDropdownList
        size="S"
        open={menuOpen}
        onClose={closeMenu}
        triggerRef={menuTriggerRef}
        anchorRef={menuTriggerRef}
        placement="bottom-end"
        portaled
        width="min"
        title={menuView === 'moveDay' ? moveToAnotherDayLabel : undefined}
        enableFlip
        autoHeight
      >
        {menuView === 'actions' && (
          <>
            {canView && (
              <ClickableListItem
                title={viewActionLabel}
                size="medium"
                startIcon={<LineEyeIcon size="S" />}
                onClick={handleViewClick}
              />
            )}
            {row.isBooked && (
              <ClickableListItem
                title={viewBookingLabel}
                size="medium"
                startIcon={<LineShoppingCartIcon size="S" />}
                onClick={handleViewBookingClick}
              />
            )}
            {!row.isBooked && !row.isManuallyBooked && row.isBookable && (
              <ClickableListItem
                title={bookLabel}
                size="medium"
                startIcon={<LineShoppingCartIcon size="S" />}
                onClick={handleBookClick}
              />
            )}
            <ClickableListItem
              title={askAiLabel}
              size="medium"
              startIcon={<AISparkleIcon size="S" />}
              onClick={handleAskAiClick}
            />
            {canMoveToAnotherDay && (
              <ClickableListItem
                title={moveToAnotherDayLabel}
                size="medium"
                startIcon={<LineArrowsSwapVerticalIcon size="S" />}
                onClick={handleMoveDayMenuOpen}
              />
            )}
            {canAskAiToMove && (
              <ClickableListItem
                title={moveWithAiLabel}
                size="medium"
                startIcon={<LineArrowsSwapVerticalIcon size="S" />}
                onClick={handleAskAiMoveClick}
              />
            )}
            <ClickableListItem
              title={noteActionLabel}
              size="medium"
              startIcon={<LinePenIcon size="S" />}
              onClick={handleEditNoteClick}
            />
            <ClickableListItem
              title={deleteLabel}
              size="medium"
              startIcon={<LineTrashAltIcon size="S" />}
              onClick={handleDeleteClick}
            />
          </>
        )}
        {menuView === 'moveDay' && moveDayOptions.map(option => (
          <TripListMoveDayMenuItem
            key={option.id}
            option={option}
            onClick={handleMoveDayOptionClick}
          />
        ))}
      </ResponsiveDropdownList>
    </RowActions>
  )
}

export default TripListMobileRowActionsMenu
